import { PERSIST_STORE_NAME } from 'constants/app.constant';
import rootReducer from './rootReducer';
import { configureStore, Middleware, Reducer, AnyAction } from '@reduxjs/toolkit';
import { persistStore, persistReducer, PersistConfig, Transform } from 'redux-persist';
import localforage from 'localforage';

// Window 타입 확장
declare global {
  interface Window {
    __REDUX_STORE__?: typeof store;
    __clearStorageData?: () => Promise<boolean>;
    __getStorageUsage?: () => Promise<StorageUsage | null>;
  }
}

// 스토리지 사용량 타입
interface StorageUsage {
  localforageBytes: number;
  localforageMB: string;
  localStorageBytes: number;
  localStorageMB: string;
}

// 모델 데이터 타입 (간소화)
interface ModelData {
  modelName?: string;
  nodes?: unknown[];
  edges?: unknown[];
  attrs?: unknown[];
  viewport?: Record<string, unknown>;
  modelConfig?: Record<string, unknown>;
  _cached?: unknown;
  _temp?: unknown;
  analysisCache?: unknown;
  history?: unknown;
  undoStack?: unknown;
  redoStack?: unknown;
  [key: string]: unknown;
}

// 상태 타입 (간소화)
interface PersistState {
  common?: {
    status?: string;
    apiListLoaded?: boolean;
    error?: string | null;
    modelInfo?: ModelData | null;
    multiModelData?: Record<string, ModelData>;
    analysisResults?: Record<string, unknown>;
    termCandidateWorkflow?: Record<string, unknown>;
    entityRefineWorkflow?: Record<string, unknown>;
    attributeRefineWorkflow?: Record<string, unknown>;
    wordCandidateWorkflow?: Record<string, unknown>;
    namingRuleWorkflow?: Record<string, unknown>;
    [key: string]: unknown;
  };
  daExtension?: Record<string, unknown>;
  [key: string]: unknown;
}

// IndexedDB 설정 (대용량 데이터 저장용)
// IndexedDB 우선, WebSQL, localStorage 순으로 fallback
// localStorage는 5MB 제한이 있지만 다른 저장소가 불가능한 환경에서 필요
const storage = localforage.createInstance({
  name: 'datamodeler-db',
  storeName: 'redux-persist',
  driver: [localforage.INDEXEDDB, localforage.WEBSQL, localforage.LOCALSTORAGE],
  description: 'Data Modeler Application Storage',
});

// 기존 persist 데이터 정리 (base 제거로 인한 마이그레이션)
const cleanupOldPersistData = async (): Promise<void> => {
  try {
    const persistKey = PERSIST_STORE_NAME;
    const existingData = (await storage.getItem(persistKey)) as PersistState | null;

    if (existingData && typeof existingData === 'object' && (existingData as { base?: unknown }).base) {
      // base 데이터 제거
      delete (existingData as { base?: unknown }).base;
      await storage.setItem(persistKey, existingData);
    }
  } catch (error) {
    console.warn('[Storage] Failed to cleanup old data:', error);
    // 정리 실패해도 앱 동작에는 영향 없음
  }
};

// Storage 초기화 및 에러 핸들링
storage
  .ready()
  .then(() => {
    // 기존에 저장된 base 데이터가 있으면 정리 (quota 초과 방지)
    // 'base'가 whitelist에서 제거되었으므로 기존 데이터 정리
    cleanupOldPersistData();
  })
  .catch((err) => {
    console.warn('Storage initialization failed, falling back to localStorage:', err);
  });

const middlewares: Middleware[] = []; // 필요하다면 다른 미들웨어는 여기에 추가

// 현재 사용 중인 스토리지가 localStorage인지 확인하는 플래그
let isUsingLocalStorage = false;
let storageCheckComplete = false;

// 스토리지 타입 확인 (초기화 후 설정)
storage
  .ready()
  .then(() => {
    const driver = storage.driver();
    isUsingLocalStorage = driver === localforage.LOCALSTORAGE;
    storageCheckComplete = true;
    if (isUsingLocalStorage) {
      console.warn('[Storage] Using localStorage (5MB limit) - large models may not persist');
    } else {
    }
  })
  .catch(() => {
    isUsingLocalStorage = true; // 실패 시 localStorage로 가정
    storageCheckComplete = true;
  });

// 데이터 크기 제한 상수
const MAX_STORAGE_SIZE_BYTES = 1.5 * 1024 * 1024; // 1.5MB (localStorage 5MB 제한에 매우 보수적)
const MAX_MODELS_FOR_STORAGE = 2; // 최대 모델 수 (매우 보수적으로)
const MAX_SINGLE_MODEL_SIZE = 200 * 1024; // 개별 모델당 최대 200KB
const SKIP_PERSIST_THRESHOLD = 4 * 1024 * 1024; // 4MB 이상이면 persist 건너뛰기

// 데이터 크기 추정 함수
const estimateSize = (obj: unknown): number => {
  try {
    return JSON.stringify(obj).length * 2; // UTF-16 기준
  } catch {
    return Infinity;
  }
};

// 모델 데이터 경량화 함수 - 큰 모델의 불필요한 데이터 제거
const lightenModel = (model: ModelData): ModelData => {
  const lightModel: ModelData = { ...model };
  // 임시 데이터나 캐시 데이터 제거
  delete lightModel._cached;
  delete lightModel._temp;
  delete lightModel.analysisCache;
  delete lightModel.history; // 히스토리 데이터 제거
  delete lightModel.undoStack; // undo 스택 제거
  delete lightModel.redoStack; // redo 스택 제거

  // 노드에서 불필요한 속성 제거 (렌더링 시 재계산됨)
  if (Array.isArray(lightModel.nodes)) {
    lightModel.nodes = lightModel.nodes.map((node) => {
      const nodeData = node as Record<string, unknown>;
      const { measured, selected, dragging, width, height, ...rest } = nodeData;
      return rest;
    });
  }

  // 엣지에서 불필요한 속성 제거
  if (Array.isArray(lightModel.edges)) {
    lightModel.edges = lightModel.edges.map((edge) => {
      const edgeData = edge as Record<string, unknown>;
      const { selected, ...rest } = edgeData;
      return rest;
    });
  }

  // attrs에서 불필요한 속성 제거
  if (Array.isArray(lightModel.attrs)) {
    lightModel.attrs = lightModel.attrs.map((attr) => {
      const attrData = attr as { items?: Array<Record<string, unknown>>; [key: string]: unknown };
      const lightAttr = { ...attrData };
      if (Array.isArray(lightAttr.items)) {
        lightAttr.items = lightAttr.items.map((item) => {
          const { _temp, _cached, ...rest } = item;
          return rest;
        });
      }
      return lightAttr;
    });
  }

  return lightModel;
};

// 모델이 저장 가능한 크기인지 확인
const isModelSaveable = (model: ModelData): boolean => {
  const size = estimateSize(model);
  if (size > MAX_SINGLE_MODEL_SIZE) {
    console.warn(
      `[Storage] Model "${model.modelName || 'unknown'}" too large (${(size / 1024).toFixed(0)}KB > ${MAX_SINGLE_MODEL_SIZE / 1024}KB), skipping persist`
    );
    return false;
  }
  return true;
};

// daExtension에서 persistWorkflow=true인 워크플로우만 필터링
const filterPersistableWorkflows = (daExtension: Record<string, unknown> | undefined): Record<string, unknown> => {
  if (!daExtension) return {};

  const result: Record<string, unknown> = {};

  // standardAnalysisWorkflow에서 persistWorkflow=true인 항목만 유지
  const standardAnalysisWorkflow = daExtension.standardAnalysisWorkflow as Record<string, { persistWorkflow?: boolean }> | undefined;
  if (standardAnalysisWorkflow) {
    const filteredStandardAnalysis: Record<string, unknown> = {};
    for (const [modelKey, workflow] of Object.entries(standardAnalysisWorkflow)) {
      if (workflow?.persistWorkflow === true) {
        filteredStandardAnalysis[modelKey] = workflow;
      }
    }
    if (Object.keys(filteredStandardAnalysis).length > 0) {
      result.standardAnalysisWorkflow = filteredStandardAnalysis;
    }
  }

  // attributeBatchWorkflow에서 persistWorkflow=true인 항목만 유지
  const attributeBatchWorkflow = daExtension.attributeBatchWorkflow as Record<string, { persistWorkflow?: boolean }> | undefined;
  if (attributeBatchWorkflow) {
    const filteredAttributeBatch: Record<string, unknown> = {};
    for (const [modelKey, workflow] of Object.entries(attributeBatchWorkflow)) {
      if (workflow?.persistWorkflow === true) {
        filteredAttributeBatch[modelKey] = workflow;
      }
    }
    if (Object.keys(filteredAttributeBatch).length > 0) {
      result.attributeBatchWorkflow = filteredAttributeBatch;
    }
  }

  return result;
};

// base 리듀서의 API 로드 상태를 제외하는 transform (워크플로우 데이터는 유지)
const createBaseTransform = (): Transform<PersistState, PersistState> => ({
  in: (state: PersistState): PersistState => {
    // Rehydrate 시 daExtension 복원
    // whitelist에 'base'가 없으므로 common은 처리하지 않음
    if (state) {
      // daExtension: persistWorkflow가 true인 워크플로우만 복원
      if (state.daExtension) {
        return {
          ...state,
          daExtension: filterPersistableWorkflows(state.daExtension),
        };
      }
    }
    return state;
  },
  out: (state: PersistState): PersistState => {
    // daExtension에서 persistWorkflow=true인 워크플로우만 저장
    // whitelist에 'base'가 없으므로 common은 처리하지 않음
    if (state) {
      const filteredDaExtension = filterPersistableWorkflows(state.daExtension);
      return {
        ...state,
        daExtension: filteredDaExtension,
      };
    }
    return state;
  },
});

const persistConfig: PersistConfig<ReturnType<typeof rootReducer>> = {
  key: PERSIST_STORE_NAME,
  keyPrefix: '',
  storage,
  // 'base' 리듀서 제외: 모델 데이터가 크면 localStorage 5MB 한계 초과
  // 모델 데이터는 Cloud 저장 또는 IndexedDB (undo/redo용)로 관리
  // theme: 다크/라이트·사이드바 접힘 등 화면 취향. 작고 새로고침 후에도 이어져야 한다.
  whitelist: ['auth', 'locale', 'daExtension', 'theme'], // 저장할 리듀서 (base 제외)
  transforms: [createBaseTransform()], // persistWorkflow 필터링을 위한 transform 적용
  writeFailHandler: (err: Error) => {
    // Storage write 실패 시 에러를 콘솔에만 기록하고 계속 진행
    if (err.name === 'QuotaExceededError' || err.message?.includes('quota')) {
      console.warn('Redux persist: Storage quota exceeded. Consider clearing old data or using Cloud storage.');
    } else {
      console.warn('Redux persist write failed:', err.message);
    }
  },
};

// Store 타입 확장 (asyncReducers 지원)
interface StoreWithAsyncReducers extends ReturnType<typeof configureStore> {
  asyncReducers: Record<string, Reducer>;
}

const store = configureStore({
  // persistReducer가 rootReducer를 감싸서 영속성 처리
  reducer: persistReducer(persistConfig, rootReducer()) as Reducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      // redux-persist 사용 시 serializableCheck 경고가 발생할 수 있으므로 false로 두는 경우가 많습니다.
      immutableCheck: false,
      serializableCheck: false,
    }).concat(middlewares),
  devTools:
    process.env.NODE_ENV === 'development'
      ? {
          name: 'MyApp',
          // redux-persist의 기본 액션 타입(예: 'persist/PERSIST', 'persist/REHYDRATE')을 제외할 수도 있습니다.
          actionsDenylist: ['persist/PERSIST', 'persist/REHYDRATE'],
        }
      : false,
}) as StoreWithAsyncReducers;

store.asyncReducers = {};

export const persistor = persistStore(store);

// 개발 모드에서 디버깅을 위해 store를 window에 노출
if (process.env.NODE_ENV === 'development') {
  window.__REDUX_STORE__ = store;
}

// 스토리지 유틸리티를 window에 노출 (개발/운영 모두에서 사용 가능)
window.__clearStorageData = async (): Promise<boolean> => {
  const { clearStorageData } = await import('./index');
  const result = await clearStorageData();
  if (result) {
    console.log('✅ 스토리지가 성공적으로 초기화되었습니다. 페이지를 새로고침해주세요.');
  }
  return result;
};

window.__getStorageUsage = async (): Promise<StorageUsage | null> => {
  const { getStorageUsage } = await import('./index');
  const usage = await getStorageUsage();
  if (usage) {
    console.log('📊 스토리지 사용량:');
    console.log(`   - localforage: ${usage.localforageMB}MB`);
    console.log(`   - localStorage: ${usage.localStorageMB}MB`);
  }
  return usage;
};

/**
 * 모든 로컬 사용자 데이터 완전 초기화 (콘솔 호출용).
 * - 앱이 잡고있는 IndexedDB 연결을 명시적으로 close → deleteDatabase 가 blocked 되지 않도록.
 * - persistor 일시정지/purge.
 * - localStorage / sessionStorage 전체 clear.
 * - 페이지 reload.
 *
 * 사용: 콘솔에서 `await window.__nukeAllUserData()` 또는 `window.__nukeAllUserData()`.
 */
(window as Window & { __nukeAllUserData?: () => Promise<void> }).__nukeAllUserData = async () => {

  // 1) 앱이 잡고있는 IndexedDB 연결 명시적으로 close
  try {
    const { historyDB } = await import('utils/historyDB');
    historyDB.close();
  } catch (e) { console.warn('  ⚠️ historyDB close 실패:', e); }

  try {
    const { modelDataDB } = await import('utils/modelDataDB');
    modelDataDB.close();
  } catch (e) { console.warn('  ⚠️ modelDataDB close 실패:', e); }

  // 2) redux-persist 일시정지 (이후 쓰기 차단) + 저장소 purge
  try {
    persistor.pause();
    await persistor.purge();
  } catch (e) { console.warn('  ⚠️ persistor purge 실패:', e); }

  // 3) 모든 IndexedDB 삭제
  let dbs: IDBDatabaseInfo[] | null = null;
  try { dbs = await indexedDB.databases?.() ?? null; } catch { dbs = null; }
  const knownNames = ['ModelHistoryDB', 'datamodeler-db', 'OpenDataModeler', 'ModelDataDB'];
  const names = dbs?.length ? dbs.map(d => d.name).filter((n): n is string => !!n) : knownNames;

  await Promise.all(names.map(name => new Promise<void>((resolve) => {
    const req = indexedDB.deleteDatabase(name);
    req.onsuccess = () => {  resolve(); };
    req.onerror   = () => { console.warn(`  ❌ error: ${name}`, req.error); resolve(); };
    req.onblocked = () => { console.warn(`  ⚠️ still blocked: ${name} (다른 탭이 열려있는지 확인)`); resolve(); };
  })));

  // 4) localStorage / sessionStorage 전체 clear
  try { localStorage.clear();  } catch (e) { console.warn(e); }
  try { sessionStorage.clear();  } catch (e) { console.warn(e); }

  setTimeout(() => window.location.reload(), 500);
};

export const injectReducer = (key: string, reducer: Reducer): StoreWithAsyncReducers | false => {
  if (store.asyncReducers[key]) {
    return false;
  }
  store.asyncReducers[key] = reducer;
  // injectReducer 호출 시에도 persistReducer로 감싸서 새로운 리듀서 포함
  store.replaceReducer(persistReducer(persistConfig, rootReducer(store.asyncReducers)) as Reducer);
  persistor.persist(); // 상태 영속화 강제 실행 (필요에 따라 사용)
  return store;
};

// 스토리지 청소 유틸리티 함수
export const clearStorageData = async (): Promise<boolean> => {
  try {
    // localforage 스토리지 클리어
    await storage.clear();

    // localStorage도 클리어 (폴백용)
    const keysToRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.includes('datamodeler')) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach((key) => localStorage.removeItem(key));

    return true;
  } catch (err) {
    console.error('[Storage] Failed to clear storage:', err);
    return false;
  }
};

// 스토리지 사용량 확인 함수
export const getStorageUsage = async (): Promise<StorageUsage | null> => {
  try {
    let totalSize = 0;

    // localforage 데이터 크기 확인
    const keys = await storage.keys();
    for (const key of keys) {
      const value = await storage.getItem(key);
      if (value) {
        totalSize += JSON.stringify(value).length * 2; // UTF-16 기준
      }
    }

    // localStorage 사용량도 확인
    let localStorageSize = 0;
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      const value = localStorage.getItem(key);
      if (key && value) {
        localStorageSize += (key.length + value.length) * 2;
      }
    }

    return {
      localforageBytes: totalSize,
      localforageMB: (totalSize / 1024 / 1024).toFixed(2),
      localStorageBytes: localStorageSize,
      localStorageMB: (localStorageSize / 1024 / 1024).toFixed(2),
    };
  } catch (err) {
    console.error('[Storage] Failed to get storage usage:', err);
    return null;
  }
};

// RootState와 AppDispatch 타입 내보내기
export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;

export default store;
